// ============================================================================
// Activos — etiquetas, tonos e iconos (épica común CC-CONSOLE ↔ WAYAK, #1638)
// ============================================================================
// Archivo común, idéntico en los dos forks. Las claves y sus etiquetas es/en
// vienen de `@torre/assets` (las mismas que guarda el API): aquí sólo se elige
// el idioma y se resuelve el tono de cada estado (skill `estados-y-tonos`).
// ============================================================================
import {
  ASSET_CATEGORIES,
  ASSET_CATEGORY_LABELS,
  ASSET_EVENT_TYPE_LABELS,
  ASSET_STATUSES,
  ASSET_STATUS_LABELS,
  ATTACHMENT_KIND_LABELS,
  FINANCE_NOTE_LABELS,
  FINANCE_STATUS_LABELS,
  MAINTENANCE_KIND_LABELS,
  MAINTENANCE_STATUS_LABELS,
  RETIRE_REASON_LABELS,
  SUPPLIER_ROLE_LABELS,
  type AssetCategory,
  type AssetEventType,
  type AssetStatus,
  type FinanceStatus,
  type Label,
  type MaintenanceStatus,
  type WarrantyStatus,
} from "@torre/assets";
import type { SelectOption, Tone } from "../../../components/ui";

export type Lang = "es" | "en";

/** Idioma de las etiquetas del paquete a partir del de i18next. */
export const langOf = (language: string | undefined): Lang => (language?.toLowerCase().startsWith("en") ? "en" : "es");

/** Etiqueta en el idioma activo; lo que el vocabulario no conoce se muestra tal cual. */
export function labelOf<K extends string>(labels: Record<K, Label>, key: string | null | undefined, lang: Lang): string {
  if (!key) return "—";
  const found = (labels as Record<string, Label | undefined>)[key];
  return found ? found[lang] : key;
}

export const categoryLabel = (key: string | null | undefined, lang: Lang) => labelOf(ASSET_CATEGORY_LABELS, key, lang);
export const statusLabel = (key: string | null | undefined, lang: Lang) => labelOf(ASSET_STATUS_LABELS, key, lang);
export const eventLabel = (key: string | null | undefined, lang: Lang) => labelOf(ASSET_EVENT_TYPE_LABELS, key, lang);
export const retireReasonLabel = (key: string | null | undefined, lang: Lang) => labelOf(RETIRE_REASON_LABELS, key, lang);
export const maintenanceKindLabel = (key: string | null | undefined, lang: Lang) => labelOf(MAINTENANCE_KIND_LABELS, key, lang);
export const maintenanceStatusLabel = (key: string | null | undefined, lang: Lang) => labelOf(MAINTENANCE_STATUS_LABELS, key, lang);
export const supplierRoleLabel = (key: string | null | undefined, lang: Lang) => labelOf(SUPPLIER_ROLE_LABELS, key, lang);
export const financeStatusLabel = (key: string | null | undefined, lang: Lang) => labelOf(FINANCE_STATUS_LABELS, key, lang);
/** Por qué una compra no está (o dejó de estar) al día en Finanzas; sin nota, nada. */
export const financeNoteLabel = (key: string | null | undefined, lang: Lang) => (key ? labelOf(FINANCE_NOTE_LABELS, key, lang) : null);
export const attachmentKindLabel = (key: string | null | undefined, lang: Lang) => labelOf(ATTACHMENT_KIND_LABELS, key, lang);

/** Opciones de un `<Select>` a partir de un vocabulario del paquete. */
export function optionsOf<K extends string>(keys: readonly K[], labels: Record<K, Label>, lang: Lang): SelectOption[] {
  return keys.map((key) => ({ value: key, label: labels[key][lang] }));
}

export const categoryOptions = (lang: Lang) => optionsOf(ASSET_CATEGORIES, ASSET_CATEGORY_LABELS, lang);
export const statusOptions = (lang: Lang) => optionsOf(ASSET_STATUSES, ASSET_STATUS_LABELS, lang);

// --- Tonos (un estado, un tono) ------------------------------------------------------

export const ASSET_STATUS_TONES = {
  en_almacen: "info",
  en_uso: "success",
  en_mantenimiento: "warning",
  baja: "neutral",
} as const satisfies Record<AssetStatus, Tone>;

export const MAINTENANCE_STATUS_TONES = {
  abierto: "warning",
  cerrado: "success",
} as const satisfies Record<MaintenanceStatus, Tone>;

export const FINANCE_STATUS_TONES = {
  pendiente: "warning",
  ligado: "success",
  desligado: "error",
  no_aplica: "neutral",
} as const satisfies Record<FinanceStatus, Tone>;

export const WARRANTY_TONES = {
  sin_garantia: "neutral",
  vigente: "success",
  por_vencer: "warning",
  vencida: "neutral",
} as const satisfies Record<WarrantyStatus, Tone>;

/** Tono de un estado; lo desconocido es `neutral`, nunca `error`. */
export function toneOf<K extends string>(tones: Record<K, Tone>, key: string | null | undefined): Tone {
  return (key && (tones as Record<string, Tone | undefined>)[key]) || "neutral";
}

export const ASSET_STATUS_ICONS = {
  en_almacen: "ri-archive-2-line",
  en_uso: "ri-user-follow-line",
  en_mantenimiento: "ri-tools-line",
  baja: "ri-forbid-line",
} as const satisfies Record<AssetStatus, string>;

export const ASSET_CATEGORY_ICONS = {
  computo: "ri-macbook-line",
  telefonia: "ri-smartphone-line",
  mobiliario: "ri-armchair-line",
  herramienta: "ri-hammer-line",
  maquinaria: "ri-settings-5-line",
  equipo_oficina: "ri-printer-line",
  otro: "ri-box-3-line",
} as const satisfies Record<AssetCategory, string>;

export const ASSET_EVENT_ICONS = {
  alta: "ri-add-circle-line",
  edicion: "ri-edit-2-line",
  asignacion: "ri-user-shared-line",
  devolucion: "ri-arrow-go-back-line",
  mantenimiento_abierto: "ri-tools-line",
  mantenimiento_cerrado: "ri-checkbox-circle-line",
  baja: "ri-forbid-line",
  finanzas: "ri-money-dollar-circle-line",
} as const satisfies Record<AssetEventType, string>;
